import { useTenantStore } from '@/store/tenant.store.js';
import { resolveListFocus } from '@/lib/notifications/resolve-list-focus.js';
import { Logger } from '@/lib/app-log.js';

const defaultGetRowId = (row) => String(row?._id ?? row?.id ?? '');

/**
 * Factory for notification row locate on paginated lists.
 * @param {object} config
 * @param {{ locateInList?: Function, getAll?: Function }} config.service — SDK service with locateInList or getAll
 * @param {import('zustand').UseBoundStore<any>} config.useStore
 * @param {(page: number, pageSize: number, search: string, sortBy: string, sortOrder: string, columnFilters: object, extraQuery?: object) => Promise<{success: boolean}>} config.fetchList
 * @param {(row: object) => string} [config.getRowId]
 * @param {(row: object, entityId: string) => boolean} [config.rowMatchesId]
 * @param {(row: object) => unknown[]} [config.getItems] — default: s => s.items
 * @param {object} [config.extraQuery]
 * @param {string} [config.defaultSortBy='']
 * @param {string} [config.defaultSortOrder='desc']
 */
export function createListLocate({
    service,
    useStore,
    fetchList,
    getRowId = defaultGetRowId,
    rowMatchesId,
    getItems = (s) => s.items,
    extraQuery = {},
    defaultSortBy = '',
    defaultSortOrder = 'desc',
}) {
    const matchRow =
        rowMatchesId ??
        ((row, entityId) => String(getRowId(row)) === String(entityId));
    return async function locateRowInList({ entityId, pageSize = 10, listQuery = {} } = {}) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId || !entityId) {
            return { found: false };
        }

        const sortBy = listQuery.sortBy ?? defaultSortBy;
        const sortOrder = listQuery.sortOrder ?? defaultSortOrder;
        const search = listQuery.search ?? '';
        const mergedExtra = { ...extraQuery, ...(listQuery.extraQuery || {}) };

        try {
            const focusParams = {
                focusEntityId: String(entityId),
                pageSize,
                tenantId,
            };
            if (sortBy) {
                focusParams.sortBy = sortBy;
                focusParams.sortOrder = sortOrder;
            }

            const locateRes = await resolveListFocus(service, focusParams);

            const focus = locateRes.focus;
            if (!focus?.found || !focus.page) {
                return { found: false };
            }

            const res = await fetchList(
                focus.page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                {},
                mergedExtra,
            );
            if (!res?.success) {
                return { found: false };
            }

            const items = getItems(useStore.getState()) || [];
            const entityIdStr = String(entityId);
            if (!items.some((row) => matchRow(row, entityIdStr))) {
                return { found: false };
            }

            return { found: true, page: focus.page };
        } catch (error) {
            Logger.error('locateRowInList failed', { error: error?.message });
            return { found: false };
        }
    };
}
